'use client';

import { createClient } from '@/lib/supabase/client';

export interface EmailSettings {
  id: string;
  enabled: boolean;
  fromName: string;
  subject: string;
  htmlTemplate: string;
  bannerUrl: string | null;
  logoUrl: string | null;
  updatedAt: string;
  raffleSubject: string;
  raffleHtmlTemplate: string;
  raffleEmailEnabled: boolean;
  blocksJson: any[];
  raffleBlocksJson: any[];
  activeTemplate: 'confirmation' | 'raffle';
}

export interface EmailTrackingSummary {
  sent: number;
  opened: number;
  bounced: number;
  clicked: number;
  openRate: number;
  clickRate: number;
}

function toEmailSettings(row: any): EmailSettings {
  return {
    id: row.id,
    enabled: row.enabled ?? false,
    fromName: row.from_name ?? 'Event Team',
    subject: row.subject ?? 'Thanks for playing!',
    htmlTemplate: row.html_template ?? '',
    bannerUrl: row.banner_url ?? null,
    logoUrl: row.logo_url ?? null,
    updatedAt: row.updated_at,
    raffleSubject: row.raffle_subject ?? 'Your Raffle Entry — Code: {{raffle_code}}',
    raffleHtmlTemplate: row.raffle_html_template ?? '',
    raffleEmailEnabled: row.raffle_email_enabled ?? true,
    blocksJson: Array.isArray(row.blocks_json) ? row.blocks_json : [],
    raffleBlocksJson: Array.isArray(row.raffle_blocks_json) ? row.raffle_blocks_json : [],
    activeTemplate: row.active_template ?? 'confirmation',
  };
}

export const TEMPLATE_FIELDS = [
  { key: '{{player_name}}', label: 'Player Name', description: "Player\'s full name" },
  { key: '{{email}}', label: 'Email', description: "Player\'s email address" },
  { key: '{{company}}', label: 'Company', description: "Player\'s company" },
  { key: '{{guess}}', label: 'Number Guess', description: 'Their manatee count guess' },
  { key: '{{manatee_time}}', label: 'Manatee Time', description: 'Find the Manatee game time (or N/A)' },
  { key: '{{games_played}}', label: 'Games Played', description: 'List of games they played' },
  { key: '{{raffle_code}}', label: 'Raffle Code', description: 'Unique raffle draw code for the player' },
];

export function applyTemplate(
  template: string,
  data: {
    playerName: string;
    email: string;
    company: string;
    guess: number;
    manateeTimeMs?: number | null;
    gamesPlayed?: string[];
    raffleCode?: string | null;
    repName?: string;
  }
): string {
  const manateeTime =
    data.manateeTimeMs != null
      ? `${(data.manateeTimeMs / 1000).toFixed(2)}s`
      : 'N/A';

  const gamesPlayed =
    data.gamesPlayed && data.gamesPlayed.length > 0
      ? data.gamesPlayed
          .map((g) => (g === 'candy-count' ? 'Count the Manatees' : 'Find the Manatee'))
          .join(', ')
      : 'None';

  return template
    .replace(/{{player_name}}/g, data.playerName)
    .replace(/{{email}}/g, data.email)
    .replace(/{{company}}/g, data.company)
    .replace(/{{guess}}/g, data.guess > 0 ? data.guess.toLocaleString() : 'N/A')
    .replace(/{{manatee_time}}/g, manateeTime)
    .replace(/{{games_played}}/g, gamesPlayed)
    .replace(/{{raffle_code}}/g, data.raffleCode ?? 'N/A')
    .replace(/{{rep_name}}/g, data.repName ?? '');
}

/**
 * Wraps the builder-generated HTML in a responsive email shell and applies
 * template variable substitution. Use this instead of applyTemplate when the
 * HTML was produced by blocksToHtml() from the drag-and-drop builder.
 */
export function applyBuilderTemplate(
  builderHtml: string,
  data: {
    playerName: string;
    email: string;
    company: string;
    guess: number;
    manateeTimeMs?: number | null;
    gamesPlayed?: string[];
    raffleCode?: string | null;
    repName?: string;
  }
): string {
  // First substitute all template variables in the raw HTML
  const substituted = applyTemplate(builderHtml, data);

  // Wrap in a minimal responsive email shell
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8" />
<meta name="viewport" content="width=device-width, initial-scale=1.0" />
<title>Email</title>
</head>
<body style="margin:0;padding:0;background:#f9fafb;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#f9fafb;">
  <tr>
    <td align="center" style="padding:24px 16px;">
      <table role="presentation" width="600" cellpadding="0" cellspacing="0" style="max-width:600px;width:100%;background:#ffffff;border-radius:12px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,0.06);">
        <tr>
          <td style="padding:0;">
            ${substituted}
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>
</body>
</html>`;
}

export const emailSettingsService = {
  async getSettings(): Promise<EmailSettings | null> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('email_settings')
      .select('*')
      .eq('id', 'default')
      .single();

    if (error || !data) return null;
    return toEmailSettings(data);
  },

  async saveSettings(settings: Partial<Omit<EmailSettings, 'id' | 'updatedAt'>>): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase.from('email_settings').upsert({
      id: 'default',
      ...(settings.enabled !== undefined && { enabled: settings.enabled }),
      ...(settings.fromName !== undefined && { from_name: settings.fromName }),
      ...(settings.subject !== undefined && { subject: settings.subject }),
      ...(settings.htmlTemplate !== undefined && { html_template: settings.htmlTemplate }),
      ...(settings.bannerUrl !== undefined && { banner_url: settings.bannerUrl }),
      ...(settings.logoUrl !== undefined && { logo_url: settings.logoUrl }),
      ...(settings.raffleSubject !== undefined && { raffle_subject: settings.raffleSubject }),
      ...(settings.raffleHtmlTemplate !== undefined && { raffle_html_template: settings.raffleHtmlTemplate }),
      ...(settings.raffleEmailEnabled !== undefined && { raffle_email_enabled: settings.raffleEmailEnabled }),
      ...(settings.blocksJson !== undefined && { blocks_json: settings.blocksJson }),
      ...(settings.raffleBlocksJson !== undefined && { raffle_blocks_json: settings.raffleBlocksJson }),
      ...(settings.activeTemplate !== undefined && { active_template: settings.activeTemplate }),
      updated_at: new Date().toISOString(),
    });

    if (error) throw new Error(error.message);
  },

  async uploadEmailAsset(file: File, type: 'banner' | 'logo'): Promise<string> {
    const supabase = createClient();
    const ext = file.name.split('.').pop();
    const path = `${type}-${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from('email-assets').upload(path, file, { upsert: true });
    if (error) throw new Error(error.message);
    const { data } = supabase.storage.from('email-assets').getPublicUrl(path);
    return data.publicUrl;
  },

  async getTrackingSummary(): Promise<EmailTrackingSummary> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('email_tracking')
      .select('event_type');

    if (error || !data) return { sent: 0, opened: 0, bounced: 0, clicked: 0, openRate: 0, clickRate: 0 };

    const counts = { sent: 0, opened: 0, bounced: 0, clicked: 0 };
    for (const row of data) {
      if (row.event_type in counts) {
        counts[row.event_type as keyof typeof counts]++;
      }
    }
    const openRate = counts.sent > 0 ? Math.round((counts.opened / counts.sent) * 100) : 0;
    const clickRate = counts.sent > 0 ? Math.round((counts.clicked / counts.sent) * 100) : 0;
    return { ...counts, openRate, clickRate };
  },

  async markEmailBounced(params: { email: string; entryId?: string; reason?: string }): Promise<void> {
    const supabase = createClient();
    await supabase.from('email_tracking').insert({
      entry_id: params.entryId ?? null,
      email: params.email,
      event_type: 'bounced',
      metadata: { reason: params.reason ?? 'manual', bounced_at: new Date().toISOString() },
    });
    const q = supabase.from('game_entries').update({ email_bounced: true });
    if (params.entryId) {
      await q.eq('id', params.entryId);
    } else {
      await q.ilike('email', params.email.trim());
    }
  },

  async recordTrackingEvent(params: {
    entryId?: string;
    email: string;
    eventType: 'sent' | 'opened' | 'bounced' | 'clicked';
    metadata?: Record<string, unknown>;
  }): Promise<void> {
    const supabase = createClient();
    await supabase.from('email_tracking').insert({
      entry_id: params.entryId ?? null,
      email: params.email,
      event_type: params.eventType,
      metadata: params.metadata ?? {},
    });
  },

  async sendConfirmationEmail(data: {
    to: string;
    fromName: string;
    subject: string;
    htmlBody: string;
    entryId?: string;
    siteUrl?: string;
  }): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase.functions.invoke('send-confirmation-email', {
      body: {
        to: data.to,
        fromName: data.fromName,
        subject: data.subject,
        htmlBody: data.htmlBody,
        entryId: data.entryId,
        siteUrl: data.siteUrl,
      },
    });
    if (error) throw new Error(error.message);
    await this.recordTrackingEvent({
      entryId: data.entryId,
      email: data.to,
      eventType: 'sent',
    });
  },

  async sendTestEmail(data: {
    to: string;
    fromName: string;
    subject: string;
    htmlBody: string;
    siteUrl?: string;
  }): Promise<void> {
    const response = await fetch('/api/send-test-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        to: data.to,
        fromName: data.fromName,
        subject: data.subject,
        htmlBody: data.htmlBody,
      }),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result?.error || 'Failed to send test email');
    }
  },
};
